import { areAllDraftReturnShipmentsBlueYonderEligible } from "@redotech/redo-model/draft-return/blue-yonder-eligibility";
import {
  DraftReturn,
  ShipmentMethodName,
} from "@redotech/redo-model/draft-return/draft-return";
import {
  getCompensationMethod,
  hasReturnItemsThatRequireShipping,
  returnOptionMethodMap,
} from "@redotech/redo-model/draft-return/util";
import { ReturnTypeEnum } from "@redotech/redo-model/return";
import { Payer } from "@redotech/redo-model/return-flow";
import { returnOptionMethodJsonFormat } from "@redotech/redo-model/return-flow/return-option";
import { Big } from "big.js";
import { ShippingEstimateData } from "../../contexts/shipping-estimate";
import { ReturnAppSettings } from "../../settings";
import { ShipmentMethodOption } from "../../shipment-method/util";

export const methodsToDisplay = [
  ShipmentMethodName.IN_STORE,
  ShipmentMethodName.SELF_SHIP,
  ShipmentMethodName.BLUE_YONDER,
  ShipmentMethodName.MANUAL_LABEL,
] as const;
export type MethodToDisplay = (typeof methodsToDisplay)[number];

export const isMethodToDisplay = (
  type: ShipmentMethodName,
): type is MethodToDisplay => {
  for (const method of methodsToDisplay) {
    if (method === type) {
      return true;
    }
  }
  return false;
};

export const skipShipmentMethodPage = async (
  draftReturn: DraftReturn,
  shippingEstimateContext: ShippingEstimateData,
  settings: ReturnAppSettings,
) => {
  const shipmentMethodOptions = await getShippingMethodOptions(
    draftReturn,
    shippingEstimateContext,
    settings,
  );
  if (
    shipmentMethodOptions.filter((method) =>
      methodsToDisplay.includes(method.type as any),
    ).length === 1
  ) {
    return true;
  }

  return false;
};

export const getShippingMethodOptions = async (
  draftReturn: DraftReturn,
  shippingEstimateContext: ShippingEstimateData,
  settings: ReturnAppSettings | undefined,
) => {
  // If no shipping is required, then skip all shipping methods
  if (!hasReturnItemsThatRequireShipping(draftReturn)) {
    console.log("shipmentMethodOptions: No shipping required");
    return [];
  }
  const compensationMethod = getCompensationMethod(draftReturn);

  if (
    draftReturn.pendingReturnItems.some((item) =>
      [...item.returnOptions].find((option) => {
        const optionMethod = returnOptionMethodJsonFormat.read(
          option.method,
        ).type;
        const compensationMethodType = compensationMethod?.type
          ? returnOptionMethodMap[compensationMethod.type]
          : undefined;
        const isManualLabel = option.manual_label === true;
        return optionMethod === compensationMethodType && isManualLabel;
      }),
    )
  ) {
    return [{ type: ShipmentMethodName.MANUAL_LABEL }];
  }

  const shippingMethodOptions: ShipmentMethodOption[] = [];

  const shipments = await shippingEstimateContext.getShipments();
  const totalShipmentFee = (await shipments).reduce((acc, shipment) => {
    return acc.add(shipment.fee);
  }, new Big(0));

  const labelPayer = compensationMethod?.payer;

  //Happy returns
  // if (
  //   shipments.length > 0 &&
  //   shipments.every((shipment) => !!shipment.happyReturnsDetails)
  // ) {
  //   const totalHappyReturnsFee = shipments.reduce((acc, shipment) => {
  //     return acc.add(shipment.happyReturnsDetails!.price);
  //   }, new Big(0));

  //   shippingMethodOptions.push({
  //     type: ShipmentMethodName.HAPPY_RETURNS,
  //     nonPickupFee:
  //       labelPayer === Payer.CUSTOMER ? totalHappyReturnsFee : new Big(0),
  //     happyReturnsDetails: {
  //       ...shipments[0]!.happyReturnsDetails!,
  //     },
  //   });

  //   shippingMethodOptions.push({
  //     type: ShipmentMethodName.SELF_SHIP,
  //     nonPickupFee:
  //       labelPayer === Payer.CUSTOMER ? totalShipmentFee : new Big(0),
  //   });
  //   return shippingMethodOptions;
  // }

  // BlueYonder returns
  if (areAllDraftReturnShipmentsBlueYonderEligible(shipments)) {
    shippingMethodOptions.push({
      type: ShipmentMethodName.BLUE_YONDER,
      nonPickupFee: new Big(0), // BlueYonder drop-off is typically free
      blueYonderDetails: {
        locations: shipments[0]!.blueYonderDetails!.locations,
        retailerCompanyId: shipments[0]!.blueYonderDetails!.retailerCompanyId,
      },
    });
  }

  const pickupFee =
    shipments?.length === 1 && shipments?.[0]?.pickupFee
      ? shipments[0].pickupFee
      : undefined;

  if (pickupFee && !draftReturn.merchantCreated) {
    shippingMethodOptions.push({
      type: ShipmentMethodName.PICKUP,
      pickupFee: new Big(pickupFee),
      nonPickupFee:
        labelPayer === Payer.CUSTOMER ? totalShipmentFee : new Big(0),
    });
  }

  // Check if return in store is available
  if (
    settings?.returnInStoreEnabled &&
    draftReturn.returnType !== ReturnTypeEnum.WARRANTY
  ) {
    shippingMethodOptions.push({ type: ShipmentMethodName.IN_STORE });
  }

  shippingMethodOptions.push({
    type: ShipmentMethodName.SELF_SHIP,
    nonPickupFee: labelPayer === Payer.CUSTOMER ? totalShipmentFee : new Big(0),
  });

  // Check if settlement is available
  // if (
  //   settings?.returns?.settlementEnabled &&
  //   settings?.returns?.settlementRefund &&
  //   settings?.returns?.settlementRefund > 0
  // ) {
  //   const orderValue = getCompensationMethod(
  //     returnContext.draftReturn,
  //   )?.totalReturnableValue;
  //   if (orderValue?.gt(0)) {
  //     shippingMethodOptions.push({ type: ShipmentMethodName.SETTLEMENT, orderValue });
  //   }
  // }
  return shippingMethodOptions;
};
